import { useCallback, useEffect, useState } from "react"
import { observer } from "mobx-react-lite"
import { useLocation, useNavigate } from "react-router"

import CitySelectorDialog from "@/components/patterns/CitySelectorDialog"
import MobileSearchDialog from "@/components/patterns/MobileSearchDialog"
import MobileStoreHeader from "@/components/patterns/MobileStoreHeader"
import MobileStoreNavigation from "@/components/patterns/MobileStoreNavigation"
import AuthDialog from "@/features/auth/AuthDialog"
import { useEmailMarketingPrompt } from "@/features/email-marketing"
import { CatalogMenu } from "@/features/catalog-menu"
import { csspCartStore } from "@/features/cssp/cart/csspCartStorage"
import basketStore from "@/features/cart/cartStore"
import { catalogSearchHistory } from "@/features/product-search/catalogSearchHistory"
import appStore from "@/stores/appStore"

import { getMobileStoreSection } from "./mobileStoreShellRoutes"

import "@/components/patterns/mobile-store-shell.css"

type MobileShellLocationState = {
  mobileStoreSearch?: boolean
}

const MobileStoreShell = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const isMobileShellActive = useMobileShellActive()
  const isAuthenticated = appStore.isLoginIn
  const locationState = (location.state ?? {}) as MobileShellLocationState
  const isSearchOpen = locationState.mobileStoreSearch === true
  const [isAuthOpen, setIsAuthOpen] = useState(() => isProtectedAccountPath(location.pathname) && !isAuthenticated)
  const [authDestination, setAuthDestination] = useState<string | null>(() =>
    isProtectedAccountPath(location.pathname) && !isAuthenticated ? getCurrentDestination(location) : null
  )
  const [isCityOpen, setIsCityOpen] = useState(() => !appStore.selectedCity)
  const [isCitiesLoading, setIsCitiesLoading] = useState(false)
  const [hasCitiesLoadError, setHasCitiesLoadError] = useState(false)
  const [recentSearches, setRecentSearches] = useState(() => catalogSearchHistory.getAll())
  const { emailMarketingDialog, openRegistrationPrompt } = useEmailMarketingPrompt()
  const isKeyboardOpen = useMobileKeyboardOpen(isMobileShellActive)
  const cartCount = basketStore.totalItems + csspCartStore.items.length
  const activeSection = getMobileStoreSection(location.pathname)

  const loadCities = useCallback(async () => {
    if (appStore.cities.length > 0) return

    setIsCitiesLoading(true)
    setHasCitiesLoadError(false)

    try {
      await appStore.getCities()
    } catch {
      setHasCitiesLoadError(true)
    } finally {
      setIsCitiesLoading(false)
    }
  }, [])

  useEffect(() => {
    if (isMobileShellActive && isCityOpen) void loadCities()
  }, [isCityOpen, isMobileShellActive, loadCities])

  useEffect(() => {
    if (isMobileShellActive && isProtectedAccountPath(location.pathname) && !isAuthenticated) {
      setAuthDestination(getCurrentDestination(location))
      setIsAuthOpen(true)
    }
  }, [isAuthenticated, isMobileShellActive, location])

  if (!isMobileShellActive) return null

  const openSearch = () => {
    setRecentSearches(catalogSearchHistory.getAll())
    navigate(getCurrentDestination(location), {
      state: { ...locationState, mobileStoreSearch: true },
    })
  }

  const closeSearch = () => {
    if (isSearchOpen) navigate(-1)
  }

  const requestAccount = () => {
    setAuthDestination("/")
    setIsAuthOpen(true)
  }

  return (
    <div className="mobile-store-shell md:hidden" data-slot="mobile-store-shell">
      <MobileStoreHeader
        city={appStore.selectedCity}
        onCityClick={() => setIsCityOpen(true)}
        onSearchClick={openSearch}
      />

      <div className="mobile-store-dock" data-keyboard-open={isKeyboardOpen || undefined}>
        <MobileStoreNavigation
          activeSection={activeSection}
          cartCount={cartCount}
          homeHref="/"
          favoritesHref="/profile/favorites"
          cartHref="/cart"
          onAccountClick={() => {
            if (!isAuthenticated) {
              requestAccount()
              return
            }

            if (activeSection === "account") {
              const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false
              window.scrollTo({ top: 0, behavior: prefersReducedMotion ? "auto" : "smooth" })
              return
            }

            navigate("/profile")
          }}
          renderCatalogTrigger={(trigger) => (
            <CatalogMenu presentation="mobile" trigger={trigger} />
          )}
        />
      </div>

      <MobileSearchDialog
        open={isSearchOpen}
        recentSearches={recentSearches}
        onOpenChange={(open) => !open && closeSearch()}
        onSubmit={(query) => {
          setRecentSearches(catalogSearchHistory.save(query))
          navigate(`/search?search=${encodeURIComponent(query)}`, { replace: true })
        }}
      />
      <CitySelectorDialog
        open={isCityOpen}
        selectedCity={appStore.selectedCity}
        isLoading={isCitiesLoading}
        hasLoadError={hasCitiesLoadError}
        onOpenChange={setIsCityOpen}
        onRetry={loadCities}
      />
      <AuthDialog
        open={isAuthOpen}
        onOpenChange={(open) => {
          setIsAuthOpen(open)
          if (!open) setAuthDestination(null)
        }}
        onAuthSuccess={(data) => {
          appStore.onAuthCompleted(data)
          const destination = authDestination || "/profile"
          setAuthDestination(null)
          if (destination !== location.pathname) navigate(destination, { replace: true })
        }}
        onRegistrationSuccess={openRegistrationPrompt}
      />
      {emailMarketingDialog}
    </div>
  )
}

const useMobileShellActive = () => {
  const query = "(max-width: 767px)"
  const getMatches = () => window.matchMedia?.(query).matches ?? true
  const [matches, setMatches] = useState(getMatches)

  useEffect(() => {
    const mediaQuery = window.matchMedia?.(query)
    if (!mediaQuery) return

    const update = () => setMatches(mediaQuery.matches)
    mediaQuery.addEventListener("change", update)
    return () => mediaQuery.removeEventListener("change", update)
  }, [])

  return matches
}

const useMobileKeyboardOpen = (enabled: boolean) => {
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    if (!enabled) return

    const viewport = window.visualViewport
    if (!viewport) return

    const update = () => {
      const focusedElement = document.activeElement
      const canOpenKeyboard =
        focusedElement instanceof HTMLInputElement ||
        focusedElement instanceof HTMLTextAreaElement ||
        focusedElement?.getAttribute("contenteditable") === "true"

      setIsOpen(canOpenKeyboard && viewport.height < window.innerHeight * 0.78)
    }

    viewport.addEventListener("resize", update)
    viewport.addEventListener("scroll", update)
    document.addEventListener("focusin", update)
    document.addEventListener("focusout", update)

    return () => {
      viewport.removeEventListener("resize", update)
      viewport.removeEventListener("scroll", update)
      document.removeEventListener("focusin", update)
      document.removeEventListener("focusout", update)
    }
  }, [enabled])

  return isOpen
}

const getCurrentDestination = (location: Pick<Location, "pathname" | "search" | "hash">) =>
  `${location.pathname}${location.search}${location.hash}`

const isProtectedAccountPath = (pathname: string) =>
  pathname.startsWith("/profile") && !pathname.startsWith("/profile/favorites")

const ObservedMobileStoreShell = observer(MobileStoreShell)

export default ObservedMobileStoreShell
